import axios from 'axios';
import {
  buildMemoryStorage,
  defaultHeaderInterpreter,
  defaultKeyGenerator,
  setupCache,
} from 'axios-cache-interceptor';
import Configuration from '@/utils/configuration';

// @ Original axios call in api file would be similar to:
// @ axios.get(`${API_HOST}/endpoint`, {
// @   headers: {
// @     Authorization: `Bearer ${localStorage.getItem('token')}`,
// @   }})

const API_HOST = Configuration.value('VUE_APP_API_HOST');
const V3_API_HOST = Configuration.value('VUE_APP_V3_API_HOST');
const UPLOADER_API_HOST = Configuration.value('VUE_APP_UPLOADER_API_HOST');

const axiosInstance = axios.create({
  baseURL: API_HOST,
  // timeout: 10000, //* 10s timeout
  // timeout: 30000, //* 30s timeout
  timeout: 60000, //* 60s timeout
});

const axiosInstanceCached = setupCache(
  axios.create({
    baseURL: API_HOST,
    timeout: 60000, //* 60s timeout
  }),
  {
    methods: ['get', 'post'],
    storage: buildMemoryStorage(),
    generateKey: defaultKeyGenerator,
    headerInterpreter: defaultHeaderInterpreter,
  }
);

// * V3 axios setup
const axiosInstanceV3 = axios.create({
  baseURL: V3_API_HOST,
  timeout: 60000, //* 60s timeout
});

const axiosInstanceCachedV3 = setupCache(
  axios.create({
    baseURL: V3_API_HOST,
    timeout: 60000, //* 60s timeout
  }),
  {
    methods: ['get', 'post'],
    storage: buildMemoryStorage(),
    generateKey: defaultKeyGenerator,
    headerInterpreter: defaultHeaderInterpreter,
  }
);

// * V3 axios uploader setup
const axiosInstanceUploader = axios.create({
  baseURL: UPLOADER_API_HOST,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

const interceptConfig = (config) => {
  // console.log(axiosInstanceCached.storage.data);
  const ghostToken = localStorage.getItem('ghostToken');
  const token = ghostToken || localStorage.getItem('token') || '';
  const isGoogleApi = config?.url && config?.url?.includes('googleapis');

  if (token && !isGoogleApi) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  //* Set { v3: true } in request params to use v3 api
  if (config.v3) {
    config.baseURL = V3_API_HOST;
  }
  //* Set { v3AuthToken: token } in request params to use v3 auth token in header
  if (config.v3AuthToken) {
    config.headers.Authorization = `Bearer ${config.v3AuthToken}`;
  }

  return config;
};

const interceptConfigV3 = (config) => {
  const { data } = config;
  if (data?.id) {
    config.id = data.id;
  }
  const ghostToken = localStorage.getItem('ghostToken');
  const token = ghostToken || localStorage.getItem('token') || '';
  const isGoogleApi = config?.url && config?.url?.includes('googleapis');

  config.baseURL = V3_API_HOST;

  if (token && !isGoogleApi) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  //* Set { v3AuthToken: token } in request params to use v3 auth token in header
  if (config.v3AuthToken) {
    config.headers.Authorization = `Bearer ${config.v3AuthToken}`;
  }

  return config;
};

const interceptConfigUploader = (config) => {
  // console.log('config=====:', config);
  return config;
};

axiosInstance.interceptors.request.use(
  (config) => {
    interceptConfig(config);

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

axiosInstanceCached.interceptors.request.use(
  (config) => {
    interceptConfig(config);

    // * setting interceptor to be able to know starting response time of the each request
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => {
    error.config.metadata.endTime = new Date();
    error.duration = error.config.metadata.endTime - error.config.metadata.startTime;

    return Promise.reject(error);
  }
);

// * setting interceptors to be able to know ending response time of the each request
axiosInstanceCached.interceptors.response.use(
  (response) => {
    response.config.metadata.endTime = new Date();
    response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
    return response;
  },
  (error) => {
    error.config.metadata.endTime = new Date();
    error.duration = error.config.metadata.endTime - error.config.metadata.startTime;
    return Promise.reject(error);
  }
);

// * V3 ====
axiosInstanceCachedV3.interceptors.request.use(
  (config) => {
    interceptConfigV3(config);

    // * setting interceptor to be able to know starting response time of the each request
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => {
    error.config.metadata.endTime = new Date();
    error.duration = error.config.metadata.endTime - error.config.metadata.startTime;

    return Promise.reject(error);
  }
);

// * V3 setting interceptors to be able to know ending response time of the each request
axiosInstanceCachedV3.interceptors.response.use(
  (response) => {
    response.config.metadata.endTime = new Date();
    response.duration = response.config.metadata.endTime - response.config.metadata.startTime;
    return response;
  },
  (error) => {
    error.config.metadata.endTime = new Date();
    error.duration = error.config.metadata.endTime - error.config.metadata.startTime;
    return Promise.reject(error);
  }
);

// * V3 interceptors request
axiosInstanceV3.interceptors.request.use(
  (config) => {
    interceptConfigV3(config);

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// * axios uploader interceptors request
axiosInstanceUploader.interceptors.request.use(
  (config) => {
    interceptConfigUploader(config);

    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// * function to delete all cached data
const deleteCache = () => {
  // console.log(axiosInstanceCached.storage.data);
  for (const prop in axiosInstanceCached?.storage?.data) {
    if (prop) {
      axiosInstanceCached.storage.remove(prop);
    }
  }
  // console.log(axiosInstanceCached.storage.data);
};

// * V3 function to delete all cached data
const deleteCacheV3 = () => {
  // console.log(axiosInstanceCachedV3.storage.data);
  for (const prop in axiosInstanceCachedV3?.storage?.data) {
    if (prop) {
      axiosInstanceCachedV3.storage.remove(prop);
    }
  }
  // console.log(axiosInstanceCachedV3.storage.data);
};

export {
  axiosInstance as default,
  axiosInstanceV3,
  axiosInstanceCached,
  axiosInstanceCachedV3,
  axiosInstanceUploader,
  deleteCache,
  deleteCacheV3,
};
